/**
 * Polyfill for requestIdleCallback
 * Allows code to run during browser idle periods
 */
export const requestIdleCallback = window.requestIdleCallback || 
    function(cb, options) {
        const start = Date.now();
        return setTimeout(() => {
            cb({
                didTimeout: false,
                timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
            });
        }, 1);
    };

export const cancelIdleCallback = window.cancelIdleCallback || 
    function(id) {
        clearTimeout(id);
    };

/**
 * Debounce a function to limit the number of calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function}
 */
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Throttle a function to limit execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in ms
 * @returns {Function}
 */
export const throttle = (func, limit) => {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

/**
 * Asynchronous save to localStorage
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 */
export const asyncLocalStorageSet = (key, value) => {
    return new Promise((resolve, reject) => {
        requestIdleCallback(() => {
            try {
                localStorage.setItem(key, JSON.stringify(value));
                resolve();
            } catch (error) {
                console.warn('Erreur lors de la sauvegarde dans localStorage:', error);
                reject(error);
            }
        }, { timeout: 100 });
    });
};

/**
 * Asynchronous retrieval from localStorage
 * @param {string} key - Storage key
 * @returns {Promise<any>}
 */
export const asyncLocalStorageGet = (key) => {
    return new Promise((resolve, reject) => {
        requestIdleCallback(() => {
            try {
                const item = localStorage.getItem(key);
                resolve(item ? JSON.parse(item) : null);
            } catch (error) {
                console.warn('Erreur lors de la lecture depuis localStorage:', error);
                reject(error);
            }
        }, { timeout: 100 });
    });
};
